import { faTriangle as faTriangleRegular } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircleQuestion,
  faTriangle as faTriangleSolid,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropertiesStore } from '@hortplus/properties-react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Col,
  OverlayTrigger,
  Popover,
  Row,
  Spinner
} from 'react-bootstrap';
import { errorToast } from '../../helpers/ToastUtils';
import { useFetch } from '../../hooks/useFetch';
import HotTrapMap from '../../maps/HotTrapMap';
import { useStores } from '../../stores/useStores';
import SeasonOverviewReportPropertySummaryGraph from './SeasonOverviewReportPropertySummaryGraph';

const SeasonOverviewReportPropertySummary = (props) => {
  const { traplogStore } = useStores();

  const propertyId = props.viewMapData?.property.id;
  const selectedInsectGroupId = props.viewMapData?.selectedInsectGroup.id;

  useEffect(() => {
    traplogStore.loadTrappingZones(
      props.bearerToken,
      props.refreshBearerToken,
      props.selectedDatabase,
      propertyId
    );
  }, [propertyId]);

  const {
    data: hotTrapStatuses,
    isFetching,
    error
  } = useFetch({
    url: `${process.env.REACT_APP_TRAPLOG_API_URL}api/properties/${propertyId}/traps-thresholds?insect_group_id=${selectedInsectGroupId}`,
    init: {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${props.bearerToken}`,
        SeasonalDatabase: props.selectedDatabase
      }
    },
    refreshBearerToken: props.refreshBearerToken
  });

  const trappingZoneResults =
    traplogStore.trappingResults[traplogStore.selectedTrappingZoneId]?.results;
  const trappingZoneResultsTraps = trappingZoneResults
    ? trappingZoneResults[trappingZoneResults.length - 1]?.traps || []
    : [];

  // The data to be passed to the summary graph.
  // First filter out the hot trap statuses for all traps in the selected trapping zone.
  // Then, map the statuses with the status 'isHot' and 'trappingZone' properties, and
  // an added 'trap' property that holds the trap information and the 'catch' amount value
  // which is calculated using the latest trapping zone results for the given trap.
  const summaryGraphData = useMemo(
    () =>
      trappingZoneResultsTraps
        ? hotTrapStatuses
            ?.filter((hts) => {
              return hts.trapping_zone === traplogStore.selectedTrappingZoneId;
            })
            .map((hts) => {
              const trap = traplogStore.trappingZones
                .find((tz) => tz.id === hts.trapping_zone)
                ?.traps?.find((trap) => trap.id === hts.id);
              return {
                isHot: hts.isHot,
                trappingZone: hts.trapping_zone,
                trap: {
                  ...trap,
                  catch: trappingZoneResultsTraps[trap?.id]?.total
                }
              };
            })
        : [],
    [
      traplogStore.trappingZones,
      traplogStore.selectedTrappingZoneId,
      hotTrapStatuses,
      trappingZoneResultsTraps
    ]
  );

  if (
    isFetching ||
    traplogStore.loadingTrappingZones ||
    traplogStore.loadingTrappingResults
  ) {
    return (
      <Row className='mb-4 mt-4' style={{ height: '485px' }}>
        <Col className='d-flex flex-column align-items-center justify-content-center'>
          <Spinner variant='primary' animation='border' />
        </Col>
      </Row>
    );
  }

  if (error) {
    errorToast(
      'Failed to load map. Could not get hot trap statuses.',
      'map-load-failed'
    );
    props.setViewMapData(null);
    return null;
  }

  return (
    <Row className={`mb-4 mt-4 summary-sticky`}>
      <Col>
        <Row>
          <Col>
            <Button
              className='float-right mb-1'
              variant='lignt'
              onClick={() => props.setViewMapData(null)}
            >
              <FontAwesomeIcon icon={faXmark} color='black' size='lg' />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <Row>
              <Col md={12} lg={6}>
                <Row>
                  <Col style={{ height: '400px' }}>
                    {traplogStore.trappingZones.length > 0 && (
                      <HotTrapMap
                        propertiesStore={props.propertiesStore}
                        onClick={(id) => {
                          traplogStore.setSelectedTrappingZoneId(id);
                        }}
                        hotTrapStatuses={hotTrapStatuses}
                      />
                    )}
                  </Col>
                </Row>
                <Row className='mt-2'>
                  <Col>
                    <Row>
                      <Col className='text-center'>
                        <div className='float-right'>
                          <FontAwesomeIcon
                            icon={faTriangleRegular}
                            color='blue'
                            className='mr-1'
                          />
                          Trapping Sites
                        </div>
                      </Col>
                      <Col className='text-center'>
                        <div className='float-left'>
                          <FontAwesomeIcon
                            icon={faTriangleSolid}
                            color='red'
                            className='mr-1'
                          />
                          Hot Traps
                          <OverlayTrigger
                            trigger={['hover', 'focus']}
                            key={'hot-trap-map-legend-info-trigger'}
                            placement={'bottom'}
                            overlay={
                              <Popover id={`hot-trap-map-legend-info-popover`}>
                                <Popover.Content>
                                  The hot trap map uses the season monitoring
                                  data to help identify traps that are catching
                                  more moths than the others on the property.
                                  These traps are marked <strong>Red</strong> as
                                  hot traps
                                </Popover.Content>
                              </Popover>
                            }
                          >
                            <FontAwesomeIcon
                              icon={faCircleQuestion}
                              color='black'
                              className='ml-1'
                            />
                          </OverlayTrigger>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col md={12} lg={6}>
                <SeasonOverviewReportPropertySummaryGraph
                  trappingZonehotTrapStatuses={summaryGraphData}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

SeasonOverviewReportPropertySummary.propTypes = {
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string,
  refreshBearerToken: PropTypes.func.isRequired,
  viewMapData: PropTypes.object,
  setViewMapData: PropTypes.func.isRequired,
  propertiesStore: PropTypes.instanceOf(PropertiesStore).isRequired
};

export default observer(SeasonOverviewReportPropertySummary);
